import { useState } from 'react';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/common/Card';
import { Badge } from '@/components/common/Badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogClose } from '@/components/common/Dialog';
import { useToast } from '@/components/common/Toast';
import { Select, SelectOption } from '@/components/common/Select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/common/Tabs';

export default function ComponentsShowcase() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState('');
  const { addToast } = useToast();

  const selectOptions: SelectOption[] = [
    { value: 'option1', label: 'Option 1' },
    { value: 'option2', label: 'Option 2' },
    { value: 'option3', label: 'Option 3' },
  ];

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Components Showcase</h1>
        <p className="text-muted-foreground">
          Test all the UI components we've built
        </p>
      </div>

      {/* Buttons */}
      <Card>
        <CardHeader>
          <CardTitle>Buttons</CardTitle>
          <CardDescription>Different button variants and sizes</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button variant="default">Default</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="destructive">Destructive</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="link">Link</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm">Small</Button>
            <Button size="default">Default</Button>
            <Button size="lg">Large</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button disabled>Disabled</Button>
          </div>
        </CardContent>
      </Card>

      {/* Toast Notifications */}
      <Card>
        <CardHeader>
          <CardTitle>Toast Notifications</CardTitle>
          <CardDescription>Show different types of toast messages</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() =>
                addToast({
                  type: 'success',
                  title: 'Success!',
                  description: 'Your action was completed successfully.',
                })
              }
            >
              Show Success
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                addToast({
                  type: 'error',
                  title: 'Error!',
                  description: 'Something went wrong. Please try again.',
                })
              }
            >
              Show Error
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                addToast({
                  type: 'info',
                  title: 'Information',
                  description: 'Here is some useful information for you.',
                })
              }
            >
              Show Info
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                addToast({
                  type: 'warning',
                  title: 'Warning!',
                  description: 'Please be careful with this action.',
                })
              }
            >
              Show Warning
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Dialog/Modal */}
      <Card>
        <CardHeader>
          <CardTitle>Dialog/Modal</CardTitle>
          <CardDescription>Open a modal dialog</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => setDialogOpen(true)}>Open Dialog</Button>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent>
              <DialogClose onClose={() => setDialogOpen(false)} />
              <DialogHeader>
                <DialogTitle>Are you sure?</DialogTitle>
                <DialogDescription>
                  This action cannot be undone. This will permanently delete your
                  account and remove your data from our servers.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setDialogOpen(false);
                    addToast({
                      type: 'success',
                      title: 'Confirmed',
                      description: 'Action completed successfully.',
                    });
                  }}
                >
                  Confirm
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>

      {/* Select Dropdown */}
      <Card>
        <CardHeader>
          <CardTitle>Select Dropdown</CardTitle>
          <CardDescription>Select from a list of options</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select
            options={selectOptions}
            value={selectedValue}
            onChange={setSelectedValue}
            placeholder="Select an option..."
          />
          {selectedValue && (
            <p className="text-sm text-muted-foreground">
              Selected: {selectOptions.find((o) => o.value === selectedValue)?.label}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Badges */}
      <Card>
        <CardHeader>
          <CardTitle>Badges</CardTitle>
          <CardDescription>Different badge variants</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Badge variant="default">Default</Badge>
            <Badge variant="secondary">Secondary</Badge>
            <Badge variant="destructive">Destructive</Badge>
            <Badge variant="outline">Outline</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <CardHeader>
          <CardTitle>Tabs</CardTitle>
          <CardDescription>Tabbed content navigation</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="tab1">
            <TabsList>
              <TabsTrigger value="tab1">Account</TabsTrigger>
              <TabsTrigger value="tab2">Password</TabsTrigger>
              <TabsTrigger value="tab3">Notifications</TabsTrigger>
            </TabsList>
            <TabsContent value="tab1" className="space-y-4">
              <h3 className="text-lg font-semibold">Account Settings</h3>
              <p className="text-sm text-muted-foreground">
                Manage your account settings and preferences.
              </p>
              <Input placeholder="Email address" type="email" />
              <Button>Save Changes</Button>
            </TabsContent>
            <TabsContent value="tab2" className="space-y-4">
              <h3 className="text-lg font-semibold">Password Settings</h3>
              <p className="text-sm text-muted-foreground">
                Update your password and security settings.
              </p>
              <Input placeholder="Current password" type="password" />
              <Input placeholder="New password" type="password" />
              <Button>Update Password</Button>
            </TabsContent>
            <TabsContent value="tab3" className="space-y-4">
              <h3 className="text-lg font-semibold">Notification Preferences</h3>
              <p className="text-sm text-muted-foreground">
                Choose how you want to receive notifications.
              </p>
              <Button variant="outline">Configure Notifications</Button>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Form Inputs */}
      <Card>
        <CardHeader>
          <CardTitle>Form Inputs</CardTitle>
          <CardDescription>Various input types</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input placeholder="Text input" />
          <Input placeholder="Email input" type="email" />
          <Input placeholder="Password input" type="password" />
          <Input placeholder="Disabled input" disabled />
        </CardContent>
      </Card>
    </div>
  );
}
